/**
 * Edge Safe Flags (F3)
 * --------------------
 * Per-subsystem flags derived from:
 *   - edgeSafeMode level (normal/soft/hard)
 *   - worldCupMobileDiscipline (battery / thermal / lowEnd / hidden)
 *   - killSwitchRegistry (operator overrides)
 *
 * Components consume these instead of duplicating the same level/density
 * branching. Pure derivation. No polling.
 */
import { useMemo } from "react";
import { useEdgeSafeMode } from "./edgeSafeMode";
import { useMobileDiscipline } from "./worldCupMobileDiscipline";
import { useKillSwitches, type KillSwitchValue } from "./killSwitchRegistry";

export interface EdgeSafeFlags {
  allowCinematic: boolean;
  allowGlow: boolean;
  allowRadarDense: boolean;
  allowCreatorHooks: boolean;
  allowAnimatedGradients: boolean;
  allowScreenshotFraming: boolean;
  allowHeavyBlur: boolean;
  allowBackdropFX: boolean;
  allowPulseFX: boolean;
  allowParticleFX: boolean;
  /** When true, viral cards / overlays / push should fully freeze. */
  screenshotFreezeMode: boolean;
  /** Convenience density 0..1 for overlay counts. */
  density: number;
}

function on(value: KillSwitchValue, allowSoft = true) {
  if (value === "off") return false;
  if (value === "soft") return allowSoft;
  return true;
}

export function useEdgeSafeFlags(): EdgeSafeFlags {
  const edge = useEdgeSafeMode();
  const md = useMobileDiscipline();
  const ks = useKillSwitches();

  return useMemo<EdgeSafeFlags>(() => {
    const hard = edge.level === "hard";
    const soft = edge.level === "soft";
    const freeze = md.hidden;

    // Base allowances from edge level + mobile discipline.
    let allowCinematic = !hard && !md.shouldReduceAnimations;
    let allowGlow = !hard && !md.shouldReduceGlow;
    let allowRadarDense = !hard && !soft && !md.lowEnd;
    let allowCreatorHooks = !hard;
    let allowAnimatedGradients = !hard && !md.shouldReduceGradients;
    let allowScreenshotFraming = !hard && !md.shouldReduceScreenshotFX;
    let allowHeavyBlur = !hard && !md.shouldReduceBlur;
    let allowBackdropFX = !hard && !md.shouldReduceBackdrop;
    let allowPulseFX = !hard && !md.shouldReduceAnimations;
    let allowParticleFX = !hard && !soft && !md.lowEnd && !md.shouldReduceAnimations;

    // Apply kill switches (operator overrides, persisted).
    // "soft" downgrades from full to limited; "off" disables.
    allowCinematic = allowCinematic && on(ks.cinematic);
    allowGlow = allowGlow && on(ks.glowEffects);
    allowRadarDense = allowRadarDense && on(ks.radarDensity);
    allowCreatorHooks = allowCreatorHooks && on(ks.creatorHooks);
    allowAnimatedGradients = allowAnimatedGradients && on(ks.animationLevel);
    allowScreenshotFraming = allowScreenshotFraming && on(ks.screenshotSystems);
    allowHeavyBlur = allowHeavyBlur && on(ks.blurEffects);
    allowBackdropFX = allowBackdropFX && on(ks.blurEffects);
    allowPulseFX = allowPulseFX && on(ks.animationLevel);
    allowParticleFX = allowParticleFX && on(ks.animationLevel);

    // Master overlay/viral kill switch flattens broad surfaces.
    if (ks.overlays === "off") {
      allowCinematic = false;
      allowCreatorHooks = false;
    }
    if (ks.viral === "off") {
      allowCreatorHooks = false;
    }
    if (ks.emotionalIntensity === "off") {
      allowGlow = false;
      allowPulseFX = false;
      allowAnimatedGradients = false;
    }

    let density = hard ? 0.35 : soft ? 0.65 : 1;
    if (md.lowEnd) density *= 0.75;
    if (md.thermal === "hot") density *= 0.6;
    if (ks.overlays === "soft") density *= 0.7;
    if (freeze) density = 0;

    return {
      allowCinematic: allowCinematic && !freeze,
      allowGlow,
      allowRadarDense,
      allowCreatorHooks: allowCreatorHooks && !freeze,
      allowAnimatedGradients: allowAnimatedGradients && !freeze,
      allowScreenshotFraming,
      allowHeavyBlur,
      allowBackdropFX,
      allowPulseFX: allowPulseFX && !freeze,
      allowParticleFX: allowParticleFX && !freeze,
      screenshotFreezeMode: freeze || ks.screenshotSystems === "off",
      density: Math.max(0, Math.min(1, density)),
    };
  }, [edge.level, md, ks]);
}